import {
  CardElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import type {
  StripeCardElement,
  StripeCardElementChangeEvent,
  StripeCardNumberElement,
} from "@stripe/stripe-js";
import {
  EnumScheduledPaymentStatus,
  ScheduledPaymentFindOneDocument,
  ScheduledPaymentFindOneQuery,
  useScheduledPaymentRetryFailedMutation,
} from "gql/generated";
import { useState } from "react";
import type { StripeCardFormChange } from "widgets/booker/shared/StripeSplitCardFields";

export type UseFailedScheduledPaymentProps = {
  failedScheduledPayment: ScheduledPaymentFindOneQuery["scheduledPaymentFindOne"];
};

export const useFailedScheduledPayment = ({
  failedScheduledPayment,
}: UseFailedScheduledPaymentProps) => {
  const elements = useElements();
  const stripe = useStripe();

  const [paymentMethodId, setPaymentMethodId] = useState<string>("");
  const [cardPostalCode, setCardPostalCode] = useState("");
  const [stripeCardElement, setStripeCardElement] = useState<
    StripeCardElement | StripeCardNumberElement | undefined
  >(undefined);

  const postalBillingDetails = (postalCode: string) => {
    const trimmed = postalCode.trim();
    return trimmed ? { address: { postal_code: trimmed } } : {};
  };

  /** Combined Stripe CardElement (postal code collected inside the iframe). */
  const handleCardElementChange = async (e: StripeCardElementChangeEvent) => {
    setCardPostalCode("");
    if (e.complete) {
      if (elements && stripe) {
        const cardElement = elements.getElement(CardElement) ?? undefined;
        setStripeCardElement(cardElement);

        if (cardElement) {
          const { error, paymentMethod } = await stripe.createPaymentMethod({
            type: "card",
            card: cardElement,
          });
          if (paymentMethod && !error) setPaymentMethodId(paymentMethod.id);
        }
      }
    } else {
      setStripeCardElement(undefined);
      setPaymentMethodId("");
    }
  };

  /** Split card form (booker has `optionalCardPostalCode` on). */
  const handleSplitCardChange = async (payload: StripeCardFormChange) => {
    setCardPostalCode(payload.postalCode);
    if (!payload.complete || !payload.cardElement) {
      setStripeCardElement(undefined);
      setPaymentMethodId("");
      return;
    }

    setStripeCardElement(payload.cardElement);

    if (elements && stripe) {
      const cardElement =
        elements.getElement(CardNumberElement) ?? payload.cardElement;
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: postalBillingDetails(payload.postalCode),
      });
      if (paymentMethod && !error) setPaymentMethodId(paymentMethod.id);
    }
  };

  const [
    retryFailedScheduledPayment,
    {
      loading: isRetryFailedScheduledPaymentLoading,
      error: retryFailedScheduledPaymentError,
    },
  ] = useScheduledPaymentRetryFailedMutation();

  const [paymentState, setPaymentState] = useState<
    "idle" | "processing" | "success" | "error"
  >("idle");
  const handlePayBalance = async () => {
    setPaymentState("processing");

    // The card-form change handler pre-creates a Stripe PaymentMethod
    // asynchronously the moment the card becomes complete. If the user
    // clicks Pay Balance before that round-trip finishes, `paymentMethodId`
    // is still empty even though the card element is mounted. Resolve it
    // inline so the backend always receives a valid `pm_…` id.
    let resolvedPaymentMethodId = paymentMethodId;
    if (
      stripeCardElement &&
      (!resolvedPaymentMethodId ||
        !resolvedPaymentMethodId.startsWith("pm_")) &&
      stripe
    ) {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: stripeCardElement,
        billing_details: postalBillingDetails(cardPostalCode),
      });
      if (error || !paymentMethod) {
        setPaymentState("error");
        return;
      }
      resolvedPaymentMethodId = paymentMethod.id;
      setPaymentMethodId(paymentMethod.id);
    }

    retryFailedScheduledPayment({
      variables: {
        scheduledPaymentId: failedScheduledPayment?._id ?? "",
        orderId: failedScheduledPayment?.order ?? "",
        paymentMethodId: resolvedPaymentMethodId ?? "",
      },
      onCompleted: data => {
        if (
          data.scheduledPaymentRetryFailed?.status ===
          EnumScheduledPaymentStatus.Success
        ) {
          setPaymentState("success");
        } else {
          setPaymentState("error");
        }
      },
      refetchQueries: [ScheduledPaymentFindOneDocument],
    });
  };

  return {
    onCardChange: handleCardElementChange,
    onSplitCardChange: handleSplitCardChange,
    isCardReady: !!stripeCardElement,
    paymentState,
    loading: isRetryFailedScheduledPaymentLoading,
    error: retryFailedScheduledPaymentError,
    handlePayBalance,
  };
};
